/**
 * Puppeteer-driven map surface for export jobs. `window.HEADLESS_API` is invoked
 * from `packages/jobs` via `page.evaluate` — methods must be async-safe and
 * wait for markers/layers before screenshots.
 */

import { pollForMapLoadComplete } from '@civilgrid/utils/src/common/pollForMapLoadComplete';
import { bbox } from '@turf/turf';
import type { MultiPolygon, Point } from 'geojson';
import { useState } from 'react';
import { FiStar } from 'react-icons/fi';
import { Marker, Source, useMap } from 'react-map-gl/mapbox';
import { transformGeoItem } from '~/api/projects2';
import { geoItemPropValueText } from '~/common/miscUtils';
import { type GeoItem, type MapDetail, MapStyle } from '~/common/types';
import { MapComponent as MapboxMap } from '~/components2/Map';
import { GeoItemMarkerDot } from '~/components2/Map/GeoItemMarkerDot';
import { FIELD_NOTES_LAYER_ID, FieldNotesLayers } from '~/pages/projectDetails/fieldNotes';
import { labelPositionForHeading } from '~/pages/projectDetails/fieldNotes/labelPositionForHeading';
import { type FieldNote, transformFieldNote } from '~/pages/projectDetails/fieldNotes/store';
import { digitizationLayerIds } from '~/store2/digitizationLayers';
import layerStore, { DEFAULT_METADATA_IDS } from '~/store2/layers';
import { UtilityLineLayers, UtilityPointAndPolygonLayers, UtilityServiceLayers } from '~/store2/TOCLayers';
import rootStore from '../../store';
import Surface from './Surface';
import store from './store';

/** Headless map page: renders export-ready map state and exposes `window.HEADLESS_API`. */
export default function Headless() {
  const map = useMap();
  const [label, setLabel] = useState('');
  const [projectGeometry, setProjectGeometry] = useState<MultiPolygon | undefined>();
  const [projectId, setProjectId] = useState<string | undefined>();
  const [potholes, setPotholes] = useState<GeoItem[] | undefined>();

  const [marker, setMarker] = useState<Point | undefined>();
  const [showScale, setShowScale] = useState(true);
  const [mapStyle, setMapStyle] = useState(MapStyle.civilGridLight);
  const [fieldNotes, setFieldNotesState] = useState<FieldNote[] | undefined>();
  const [fieldNotesMapMode, setFieldNotesMapMode] = useState<'summary' | 'detail' | null>(null);
  const [potholesMapMode, setPotholesMapMode] = useState<'summary' | 'detail' | null>(null);
  /** Mapbox bearing for PDF summary maps (0 = north up). */
  const [summaryMapBearing, setSummaryMapBearingState] = useState(0);

  const MAX_MAP_ZOOM = 20;
  /** PDF per-item detail maps (field notes, potholes) — tight close-up. */
  const EXPORT_DETAIL_MAP_ZOOM = 21;
  const EXPORT_DETAIL_MAP_PADDING = 0;
  /** HTML marker dot for pothole detail screenshots (~2.5× summary size). */
  const POTHOLE_EXPORT_DETAIL_DOT_SIZE = 52;
  /** PH# labels on multi-pothole summary PDF maps (matches field-note export labels). */
  const POTHOLE_EXPORT_SUMMARY_LABEL_FONT_SIZE = '1rem';

  /** Resolves after `ms` milliseconds (pacing between export setup steps). */
  const waitMs = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  /** Waits until the headless map finishes any in-flight camera move (`moveend`). */
  async function waitForMapMoveEnd() {
    const mapRef = map.headlessMap;
    if (!mapRef) return;
    await new Promise<void>((resolve) => {
      if (!mapRef.isMoving()) {
        resolve();
        return;
      }
      mapRef.once('moveend', () => resolve());
    });
  }

  /**
   * Polls the DOM until `expected` summary-map field-note labels
   * (`data-testid="field-note-export-label-*"`) are present. Throws after ~4s.
   */
  async function waitForExportLabelCount(expected: number) {
    for (let attempt = 0; attempt < 40; attempt++) {
      const count = document.querySelectorAll('[data-testid^="field-note-export-label-"]').length;
      if (count === expected) {
        return;
      }
      await waitMs(100);
    }
    const count = document.querySelectorAll('[data-testid^="field-note-export-label-"]').length;
    throw new Error(`Expected ${expected} field note export label(s), found ${count}`);
  }

  /**
   * Polls Mapbox until the field-notes circle layer has `expected` rendered features.
   * Used when switching between summary and detail export modes.
   */
  async function waitForFieldNoteLayerFeatureCount(expected: number) {
    for (let attempt = 0; attempt < 40; attempt++) {
      const mapRef = map.headlessMap?.getMap();
      //@ts-expect-error
      const count = mapRef?.queryRenderedFeatures(undefined, { layers: [FIELD_NOTES_LAYER_ID] })?.length ?? 0;
      if (count === expected) {
        return;
      }
      await waitMs(100);
    }
    const mapRef = map.headlessMap?.getMap();
    //@ts-expect-error
    const count = mapRef?.queryRenderedFeatures(undefined, { layers: [FIELD_NOTES_LAYER_ID] })?.length ?? 0;
    throw new Error(`Expected ${expected} field note map feature(s), found ${count}`);
  }

  /**
   * Polls the DOM until `expected` pothole HTML markers (`data-testid="pothole-marker-*"`)
   * are mounted. Throws after ~4s.
   */
  async function waitForPotholeMarkerCount(expected: number) {
    for (let attempt = 0; attempt < 40; attempt++) {
      const count = document.querySelectorAll('[data-testid^="pothole-marker-"]').length;
      if (count === expected) {
        return;
      }
      await waitMs(100);
    }
    const count = document.querySelectorAll('[data-testid^="pothole-marker-"]').length;
    throw new Error(`Expected ${expected} pothole map marker(s), found ${count}`);
  }

  /**
   * Frame export items in the headless map. Summary exports pass `bearing` from
   * `decideSummaryMapCamera`; detail exports use zoom 21 and bearing 0.
   */
  function fitToFeatures(
    items: Array<{ geometry: GeoItem['geometry'] }>,
    { padding, zoom, bearing }: { padding: number; zoom?: number; bearing: number },
  ) {
    if (items.length === 0) return;
    const mapRef = map.headlessMap;
    if (!mapRef) return;

    // Single points have no bbox extent — flyTo for a tight close-up.
    if (items.length === 1 && items[0]?.geometry?.type === 'Point') {
      const [lng, lat] = items[0].geometry.coordinates;
      const mapMaxZoom = mapRef.getMaxZoom();
      const targetZoom = Math.min(zoom ?? MAX_MAP_ZOOM, mapMaxZoom);
      mapRef.flyTo({ center: [lng, lat], zoom: targetZoom, bearing, duration: 0 });
      return;
    }

    const _bbox = bbox({
      type: 'FeatureCollection',
      features: items.map((item) => ({
        type: 'Feature',
        geometry: item.geometry,
        properties: {},
      })),
    });
    //@ts-expect-error
    mapRef.fitBounds(_bbox, { padding, bearing, duration: 0, maxZoom: MAX_MAP_ZOOM });
    if ((mapRef.getZoom() ?? 0) > MAX_MAP_ZOOM) {
      mapRef.setZoom(MAX_MAP_ZOOM);
    }
  }

  /**
   * Imperative API on `window` for Puppeteer `page.evaluate` in export and print jobs.
   * Async methods should be awaited from jobs after changes that affect the screenshot.
   */
  // @ts-expect-error
  window.HEADLESS_API = {
    /** Mapbox `MapRef` for jobs that need direct camera or style control. */
    map: map.headlessMap,

    /** Sleeps for the given number of seconds. */
    wait: (seconds: number) => {
      return new Promise((resolve) => {
        setTimeout(resolve, seconds * 1000);
      });
    },

    /** Polls until `map.headlessMap` reports `loaded()`. */
    waitForMapLoadComplete: () =>
      pollForMapLoadComplete(
        () => map.headlessMap?.loaded() ?? false,
        // @ts-expect-error
        (seconds) => window.HEADLESS_API.wait(seconds),
      ),

    /** Sets the active organization in the client root store (layer/metadata scope). */
    setOrganization: (orgId: string) => {
      rootStore.setCurrentOrganization(orgId);
    },

    /** Sets the project id passed into the headless `MapboxMap` (tile/layer context). */
    setProjectId: (projectId?: string) => {
      setProjectId(projectId);
    },

    /** Clears all visible TOC layer ids. */
    turnOffAllLayers: () => {
      layerStore.visibleLayerIds.clear();
    },

    /** Replaces visible metadata layer ids with the given list. */
    setMetadataIds: (metadataIds: string[]) => {
      layerStore.visibleMetadataIds.clear();
      metadataIds.forEach((id) => {
        layerStore.visibleMetadataIds.add(id);
      });
    },

    /** Clears all visible metadata layer ids. */
    turnOffAllMetadata: () => {
      layerStore.visibleMetadataIds.clear();
    },

    /** Enables the default metadata ids from the layer store. */
    turnOnDefaultMetadata: () => {
      DEFAULT_METADATA_IDS.forEach((id) => {
        layerStore.visibleMetadataIds.add(id);
      });
    },

    /** Shows utility and digitization layers only (hides a few noisy basemap overlays). */
    turnOnUtilityLayersOnly: () => {
      layerStore.visibleLayerIds.clear();
      UtilityLineLayers.forEach((l) => {
        layerStore.visibleLayerIds.add(l);
      });
      UtilityPointAndPolygonLayers.forEach((l) => {
        layerStore.visibleLayerIds.add(l);
      });
      UtilityServiceLayers.forEach((l) => {
        layerStore.visibleLayerIds.add(l);
      });
      digitizationLayerIds.forEach((l) => {
        layerStore.visibleLayerIds.add(l);
      });

      layerStore.visibleLayerIds.delete('gasDistanceArrow');
      layerStore.visibleLayerIds.delete('pgeParcelBasemap');
      layerStore.visibleLayerIds.delete('fiberLitBuildings');
    },

    /** Replaces visible TOC layer ids with the given list. */
    setLayers: (layers: string[]) => {
      layerStore.visibleLayerIds.clear();
      layers.forEach((l) => {
        layerStore.visibleLayerIds.add(l);
      });
    },

    /** Renders a large overlay label in the top-left (debug / exhibit titles). */
    setLabel: (label: string) => {
      setLabel(label);
    },

    /** Loads digitized map objects into the headless surface store. */
    setObjects: (objects: MapDetail['objects']) => {
      store.setObjects(objects);
    },

    /** Sets the project boundary shown on the headless map. */
    setProjectGeometry: (geometry?: MultiPolygon) => {
      setProjectGeometry(geometry);
    },

    /** Switches to the regional key-map style, centers on `point`, and hides the scale and logo controls. */
    setKeyMap: (point: Point) => {
      map.headlessMap?.getMap().setStyle('mapbox://styles/agoramaps21/clwj8eycv01mc01qo8n3qd7xd');
      setMarker(point);
      map.headlessMap?.setZoom(11);
      map.headlessMap?.setCenter({
        lng: point.coordinates[0],
        lat: point.coordinates[1],
      });
      setShowScale(false);
      (document.getElementsByClassName('mapboxgl-ctrl-logo') as HTMLCollectionOf<HTMLElement>)[0].style.display =
        'none';
    },

    /** Restores the default CivilGrid light style and map controls after `setKeyMap`. */
    resetKeyMap: () => {
      map.headlessMap?.getMap().setStyle(MapStyle.civilGridLight);
      setMarker(undefined);
      setShowScale(true);
      (document.getElementsByClassName('mapboxgl-ctrl-logo') as HTMLCollectionOf<HTMLElement>)[0].style.display =
        'block';
    },

    /** Changes the Mapbox style URL used by the headless map. */
    setMapStyle: (style: MapStyle) => {
      setMapStyle(style);
    },

    /** Returns rendered features for all currently active TOC layers. */
    getFeatureData() {
      const features = map.headlessMap?.queryRenderedFeatures(undefined as any, {
        layers: layerStore.activeLayerIds,
      });
      if (features) {
        return features.map((f) => {
          return {
            source: f.source,
            properties: f.properties,
            geometry: f.geometry,
          };
        });
      } else {
        return [];
      }
    },

    /** Sets map bearing for summary PDF maps (0 = north up); rotates the north-arrow overlay. */
    setSummaryMapBearing(bearing: number) {
      setSummaryMapBearingState(bearing);
    },

    /** All selected potholes for the report summary map (labels on, wide padding). */
    async setPotholes(geoItems: GeoItem[], mapBearing = 0) {
      setPotholesMapMode('summary');
      const transformed = geoItems.map(transformGeoItem);
      setPotholes(transformed);
      fitToFeatures(transformed, { padding: 200, bearing: mapBearing });
      await waitForMapMoveEnd();
      await waitMs(300);
      await waitForPotholeMarkerCount(transformed.length);
    },

    /** One pothole for PDF detail maps — clears other pins, waits for render, max zoom. */
    async preparePotholeDetailMap(item: GeoItem) {
      setSummaryMapBearingState(0);
      setProjectId(undefined);
      setProjectGeometry(undefined);
      store.setObjects({});
      setFieldNotesMapMode(null);
      setFieldNotesState(undefined);
      setPotholesMapMode(null);
      setPotholes(undefined);

      await waitMs(400);
      await waitForPotholeMarkerCount(0);

      const transformed = transformGeoItem(item);
      setPotholesMapMode('detail');
      await waitMs(50);
      setPotholes([transformed]);

      await waitMs(500);
      fitToFeatures([transformed], {
        padding: EXPORT_DETAIL_MAP_PADDING,
        zoom: EXPORT_DETAIL_MAP_ZOOM,
        bearing: 0,
      });
      await waitForMapMoveEnd();
      await waitMs(600);
      await waitForPotholeMarkerCount(1);
    },

    /** Removes pothole markers and exits summary/detail pothole map mode. */
    clearPotholes() {
      setPotholesMapMode(null);
      setPotholes(undefined);
    },

    /** Field notes + images for exhibit summary map (numbered labels when `item.i` is set). */
    async setFieldNotes(items: any[], mapBearing = 0) {
      setFieldNotesMapMode('summary');
      const transformed = items.map(transformFieldNote) as FieldNote[];
      setFieldNotesState(transformed);
      fitToFeatures(transformed, { padding: 200, bearing: mapBearing });
      await waitForMapMoveEnd();
      await waitForFieldNoteLayerFeatureCount(transformed.length);
      await waitForExportLabelCount(transformed.filter((item) => item.i != null).length);
      await waitMs(300);
    },

    /** One note for PDF detail maps — clears all other pins, waits for render, max zoom. */
    async prepareFieldNoteDetailMap(item: any) {
      setSummaryMapBearingState(0);
      setProjectId(undefined);
      setProjectGeometry(undefined);
      store.setObjects({});
      setPotholesMapMode(null);
      setPotholes(undefined);
      setFieldNotesMapMode(null);
      setFieldNotesState([]);

      // Let React unmount summary-map markers/layers before showing the single note.
      await waitMs(400);
      await waitForExportLabelCount(0);
      await waitForFieldNoteLayerFeatureCount(0);

      const transformed = transformFieldNote(item) as FieldNote;
      setFieldNotesMapMode('detail');
      setFieldNotesState([transformed]);

      await waitMs(400);
      fitToFeatures([transformed], {
        padding: EXPORT_DETAIL_MAP_PADDING,
        zoom: EXPORT_DETAIL_MAP_ZOOM,
        bearing: 0,
      });
      await waitForMapMoveEnd();
      await waitMs(600);
      await waitForFieldNoteLayerFeatureCount(1);
    },

    /** Removes field-note layers/labels and exits summary/detail field-note map mode. */
    clearFieldNotes() {
      setFieldNotesMapMode(null);
      setFieldNotesState(undefined);
    },
  };

  return (
    <div id="map-container" className="flex h-full w-full">
      <MapboxMap
        id="headlessMap"
        store={store}
        mapStyle={mapStyle}
        showLayers={true}
        showLayerPicker={false}
        showLegend={false}
        showBasemapPicker={false}
        showZoomButtons={false}
        showScale={showScale}
        // Headless is an export-only surface; the out-of-region UI banner must never
        // be baked into screenshots (regression from moving exports to the headless
        // pipeline in #2632). Keep parity with the drf-app headless route, which omits it.
        isOutOfRegionDisabled={true}
        measureTool={false}
        printTool={false}
        allowRotation={false}
        projectGeometry={projectGeometry}
        showAttribution={false}
        projectId={projectId}
      >
        <Surface interactive={false} />
        {marker && (
          <Marker longitude={marker.coordinates[0]} latitude={marker.coordinates[1]}>
            <FiStar size={96} className="fill-red-600 stroke-none" />
          </Marker>
        )}

        {potholes && potholes.length > 0 && (
          <Source
            id="potholes"
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: [],
            }}
          >
            {potholes.map((item) => {
              const [lng, lat] = item.geometry.coordinates;
              const isDetail = potholesMapMode === 'detail';
              return (
                <Marker
                  key={item.id}
                  longitude={lng}
                  latitude={lat}
                  anchor="center"
                  onClick={() => {}}
                  style={{
                    fontSize: '1rem',
                    zIndex: 10,
                    cursor: 'pointer',
                  }}
                >
                  <GeoItemMarkerDot
                    testId={`pothole-marker-${item.id}`}
                    label={
                      isDetail
                        ? undefined
                        : geoItemPropValueText(item.fields['PH#']?.value || item.fields['MH#']?.value)
                    }
                    fill={item.fields?.Priority?.value === 'High' ? 'red' : 'yellow'}
                    stroke="lightgreen"
                    labelFontSize={isDetail ? undefined : POTHOLE_EXPORT_SUMMARY_LABEL_FONT_SIZE}
                    dotSize={isDetail ? POTHOLE_EXPORT_DETAIL_DOT_SIZE : 20}
                  />
                </Marker>
              );
            })}
          </Source>
        )}

        {fieldNotes && fieldNotes.length > 0 && (
          <>
            <FieldNotesLayers
              fieldNotes={fieldNotes}
              hoveredItem={null}
              mapRenderMode={fieldNotesMapMode === 'detail' ? 'exportDetail' : 'default'}
            />
            {fieldNotesMapMode === 'summary' &&
              fieldNotes
                .filter((item) => item.i != null)
                .map((item) => {
                  const [lng, lat] = item.geometry.coordinates;
                  // Photo items with a compass heading: flip the label to the
                  // opposite side of the dot so it can't sit on top of the cone.
                  const heading = item.type === 'image' ? item.files?.[0]?.compassHeading : undefined;
                  // The map is rotated by `summaryMapBearing` and cones rotate
                  // with it, so place the label opposite the cone in screen space.
                  const { anchor, offset } = labelPositionForHeading(heading, summaryMapBearing);
                  return (
                    <Marker key={item.id} longitude={lng} latitude={lat} anchor={anchor} offset={offset}>
                      <div
                        data-testid={`field-note-export-label-${item.id}`}
                        style={{
                          backgroundColor: 'white',
                          padding: '2px 6px',
                          borderRadius: 4,
                          border: '1px solid #444',
                          fontSize: '1rem',
                          fontWeight: 700,
                          lineHeight: 1,
                          color: '#111',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.i}
                      </div>
                    </Marker>
                  );
                })}
          </>
        )}
      </MapboxMap>

      {/* show north image on every map except the high level region map i.e only map with marker present */}
      {!marker && (
        <div className="absolute right-4 top-4 z-10 h-[10vh] w-[10vw] p-1">
          <img
            src={'/north.png'}
            alt={'north-arrow'}
            className="h-full w-full object-contain"
            // Counter-rotate so "north" on the asset matches map bearing from jobs layout.
            style={{ transform: `rotate(${-summaryMapBearing}deg)` }}
          />
        </div>
      )}

      {label && <div className="absolute left-0 top-0 m-2 rounded bg-black/60 p-2 text-4xl text-white">{label}</div>}
    </div>
  );
}
